import type { ApiResponse } from "@shared/contracts";
import { useCustomMutation, type WriteApiOptions } from "@shared/api/hooks";
import { api, type ApiError } from "@shared/api";
import { AUTH_LOGOUT_SERVICE_NAME } from "../constants";

export type UseLogoutApiOptions = Omit<
  WriteApiOptions<null, void>,
  "serviceName" | "idRequired" | "invalidateKeys"
>;

/**
 * Log out the current token — **`GET`** `/v1/{actor}/logout`.
 *
 * The verb is the backend's choice (`routes/v1/api/admin.php`), which is why
 * this is a hand-rolled mutation rather than `usePostApi`.
 *
 * This only performs the server call. Clearing local state is the caller's
 * job: revoke the persisted session AND wipe the query cache
 * (`queryClient.clear()`) so nothing leaks into the next session.
 * `useLogout` in `features/auth/hooks` does both.
 *
 * Note that with `JWT_BLACKLIST_ENABLED=false` the token stays valid until it
 * expires, so the client-side clear is what actually ends the session.
 */
export function useLogoutApi(options?: UseLogoutApiOptions) {
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useCustomMutation<ApiResponse<null>, ApiError, void>({
    ...mutationOptions,
    mutationFn: () => api.get<null>(AUTH_LOGOUT_SERVICE_NAME),
    onSuccess: (response, variables) => {
      onSuccess?.(response.data, response.message, variables);
    },
  });
}
